/**
 * The working status of a lead — how the sales side of the platform tracks it.
 * Kept intentionally small: every value maps to a decision the operator makes
 * right after a conversation. Converted, Cold and Spam are "get it out of my way"
 * buckets and are hidden from the lead list by default.
 */
export enum PlatformLeadStageEnum {
  /** Never handled — the default for a fresh signup or inbound message */
  New = 'new',
  /** Actively being worked: talked to them, following up */
  InProgress = 'in_progress',
  /** Parked on purpose — waiting on them, on a feature, or on a date */
  OnHold = 'on_hold',
  /** Finished with it, whatever the outcome */
  Done = 'done',
  /** Became a paying customer — nothing left to work. Hidden by default */
  Converted = 'converted',
  /** Not interested / went quiet. Hidden by default */
  Cold = 'cold',
  /** Not a real lead at all. Hidden by default */
  Spam = 'spam',
}

/** Statuses shown in the lead list unless the operator explicitly asks for the rest */
export const DEFAULT_VISIBLE_STAGES: PlatformLeadStageEnum[] = [
  PlatformLeadStageEnum.New,
  PlatformLeadStageEnum.InProgress,
  PlatformLeadStageEnum.OnHold,
  PlatformLeadStageEnum.Done,
];

/** Statuses that still need attention — they drive the "open" counters */
export const OPEN_STAGES: PlatformLeadStageEnum[] = [
  PlatformLeadStageEnum.New,
  PlatformLeadStageEnum.InProgress,
];

/** Statuses hidden from the list unless asked for */
export const HIDDEN_STAGES: PlatformLeadStageEnum[] = [
  PlatformLeadStageEnum.Converted,
  PlatformLeadStageEnum.Cold,
  PlatformLeadStageEnum.Spam,
];

/**
 * Values written by the previous funnel-shaped model. Migrated once on boot;
 * kept here so old documents (and any stale client) resolve to a real status.
 */
export const LEGACY_STAGE_MAP: Record<string, PlatformLeadStageEnum> = {
  contacted: PlatformLeadStageEnum.InProgress,
  demo: PlatformLeadStageEnum.InProgress,
  // NB: 'converted' is a real status again — it must NOT be listed here, or the
  // boot-time migration would rewrite every converted lead back to Done.
  free: PlatformLeadStageEnum.Done,
  churned: PlatformLeadStageEnum.Done,
  not_relevant: PlatformLeadStageEnum.Cold,
};

export const normalizeStage = (stage?: string): PlatformLeadStageEnum => {
  if (!stage) return PlatformLeadStageEnum.New;
  if (Object.values(PlatformLeadStageEnum).includes(stage as PlatformLeadStageEnum)) {
    return stage as PlatformLeadStageEnum;
  }
  return LEGACY_STAGE_MAP[stage] || PlatformLeadStageEnum.New;
};
